import { useCookieState } from 'ahooks'
import { Button } from 'antd'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'
import { useIntercom } from 'react-use-intercom'

import { ResultWithIllustration } from '../common/result'
import { HttpMethods } from '@/config/urls'
import { referralUrls } from '@/config/urls/account/referral'
import { useNotificationContext } from '@/context/NotificationContext'
import { useWorkspaceContext } from '@/context/WorkspaceContext'
import { COOKIES, httpFetch } from '@/utils/http'
import messages, { buildI18nFunction, IntlFunction } from '@/utils/messages'

import { Workspace } from '@/types/index.d'

export declare type WorkspaceStatusAlertProps = {
  status: Workspace['status']
}

const COMPONENT_ID = 'WorkspaceStatusAlert'

export function WorkspaceStatusAlert({
  status,
}: WorkspaceStatusAlertProps): JSX.Element {
  const { showNewMessage: showIntercom } = useIntercom()
  const { notification } = useNotificationContext()

  const { workspaceId } = useWorkspaceContext()
  const [token] = useCookieState(COOKIES.XSRF_TOKEN)
  const intl = useIntl()
  const router = useRouter()
  const t = buildI18nFunction(intl.formatMessage)

  const handleContactUsClick = () => {
    showIntercom()
  }

  const handleFreeplanClick = () => {
    httpFetch(referralUrls.getDowngrade({ workspaceId }), {
      method: HttpMethods.PATCH,
      xsrfToken: token,
    })
      .then((res) => {
        if (res.ok) {
          notification.success({
            message: t(`${COMPONENT_ID}.buttons.freeplan.success.message`),
          })
          setTimeout(() => router.reload(), 5000)
        } else {
          notification.error({
            message: t(`${COMPONENT_ID}.buttons.freeplan.error.message`),
          })
        }
      })
      .catch(() =>
        notification.error({
          message: t(`${COMPONENT_ID}.buttons.freeplan.error.message`),
        })
      )
  }

  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        height: '100vh',
        justifyContent: 'center',
        margin: '0 auto',
        position: 'relative',
        top: '-6em',
        width: '480px',
      }}
    >
      <ResultWithIllustration
        icon={
          <Image
            alt="archived"
            src="/images/illustrations/archived.svg"
            width={480}
            height={480}
          />
        }
        title={t(`${COMPONENT_ID}.${status}.title` as keyof typeof messages)}
        subTitle={t(
          `${COMPONENT_ID}.${status}.subTitle` as keyof typeof messages
        )}
        extra={
          <AlertButtons
            componentid={COMPONENT_ID}
            onContactUsClick={handleContactUsClick}
            onFreeplanClick={handleFreeplanClick}
            t={t}
            workspaceStatus={status}
          />
        }
      />
    </div>
  )
}

type AlertButtonsType = {
  componentid: `WorkspaceStatusAlert`
  onContactUsClick: () => void
  onFreeplanClick: () => void
  t: IntlFunction
  workspaceStatus: Workspace['status']
}

export function AlertButtons({
  onContactUsClick,
  onFreeplanClick,
  t,
  workspaceStatus,
  componentid,
}: AlertButtonsType) {
  const result = [
    <Button
      data-cy={`${componentid}.button.contactUs`}
      key="contact-us"
      onClick={onContactUsClick}
      type="primary"
    >
      {t(`${componentid}.buttons.contactus`)}
    </Button>,
  ]
  const freeplanButtonExist = 'EXPIRED' === workspaceStatus

  if (freeplanButtonExist)
    result.unshift(
      <Button
        data-cy={`${componentid}.button.freePlan`}
        key="free-plan"
        onClick={onFreeplanClick}
        type="default"
      >
        {t(`${componentid}.buttons.freeplan`)}
      </Button>
    )

  return result
}
